import React, { forwardRef, useImperativeHandle, useRef } from 'react';
import { List, AutoSizer, ListRowProps } from 'react-virtualized';
import Meme from './Meme';
import DynamicAdSense from '../ads/DynamicAdSense';
import { Box } from '@mui/material';
import { PostType, CommentType } from '../memeverse_types';

export interface VirtualizedFeedHandle {
    scrollToNext: () => void;
    scrollToPrev: () => void;
}

interface VirtualizedFeedProps {
    posts: PostType[];
    allComments: Record<string, CommentType>;
    onReplyClick: (item: PostType | CommentType) => void;
    onLoadMore: () => void;
    hasMore: boolean;
    isNewsFeed?: boolean;
}

const VirtualizedFeed = forwardRef<VirtualizedFeedHandle, VirtualizedFeedProps>((
{
    posts,
    allComments,
    onReplyClick,
    onLoadMore,
    hasMore,
    isNewsFeed = false,
}, ref) => {
    const listRef = useRef<List>(null);
    const currentRowRef = useRef(0);
    const rowHeightRef = useRef(0);

    useImperativeHandle(ref, () => ({
        scrollToNext: () => {
            const next = Math.min(currentRowRef.current + 1, posts.length - 1);
            listRef.current?.scrollToRow(next);
            currentRowRef.current = next;
        },
        scrollToPrev: () => {
            const prev = Math.max(currentRowRef.current - 1, 0);
            listRef.current?.scrollToRow(prev);
            currentRowRef.current = prev;
        },
    }));

    const rowRenderer = ({ index, key, style, parent }: ListRowProps) => {
        const post = posts[index];

        // Check if it's an Ad placeholder
        if (post.docId && post.docId.startsWith('ad-')) {
            return (
                <div style={style} key={key}>
                    <DynamicAdSense adType='auto' />
                </div>
            );
        }

        return (
            <div style={style} key={key}>
                <Meme
                    post={post}
                    allComments={allComments}
                    onReplyClick={onReplyClick}
                    isNewsFeed={isNewsFeed}
                />
            </div>
        );
    };

    const handleRowsRendered = ({ stopIndex }: { stopIndex: number }) => {
        if (hasMore && stopIndex >= posts.length - 3) {
            onLoadMore();
        }
    };

    return (
        <Box sx={{ width: '100%', height: '100%' }}>
            <AutoSizer>
                {({ width, height }) => {
                    rowHeightRef.current = height;
                    return (
                        <List
                            ref={listRef}
                            width={width}
                            height={height}
                            rowCount={posts.length}
                            rowHeight={height} // Full screen items
                            rowRenderer={rowRenderer}
                            onRowsRendered={handleRowsRendered}
                            onScroll={({ scrollTop }) => {
                                if (height > 0) {
                                    currentRowRef.current = Math.round(scrollTop / height);
                                }
                            }}
                            style={{ outline: "none" }}
                            className="virtualized-feed-list"
                        />
                    );
                }}
            </AutoSizer>
        </Box>
    );
});

VirtualizedFeed.displayName = 'VirtualizedFeed';

export default VirtualizedFeed;
